/**
 * From the dialog's form values to the connection it saves or probes (moved out of `NewConnectionPopup`, C0).
 */
import type { Connection, SaveConnectionOptions } from '../../../../types/electron';
import { buildProbeConnectionId } from '../../../../types/probeConnectionId';
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { withoutTransientStorageFields } from '../../components/azureStorageSharePath';
import { getDecryptedPassword } from '../../services/connectionService';
import { oracleConnectMode } from './connectionFieldRules';
import { oracleDescriptorAddress } from '../../../../types/targetSecrets';
import { withoutKustoProperties } from '../../../../core/engines/kustoUrl';

/**
 * Whether a result's fingerprint reads the password as typed. Create mode: always (C0) — a restored draft's password is
 * never taken for "the saved password". Edit mode (round B): only once the user edited it; a pristine field dials with
 * the saved password, so hydrating it is no edit.
 */
export function fingerprintsLiteralPassword(isEditMode: boolean, passwordDirty: boolean | undefined): boolean {
  return !isEditMode || passwordDirty === true;
}

export function normalizeValues(values: ConnectionFormValues): ConnectionFormValues {
  return {
    ...values,
    entraTenantId: values.entraTenantId?.trim() || undefined,
    database: values.database?.trim() || '',
    color: values.color || undefined,
  };
}

type TnsTarget = Pick<Partial<ConnectionFormValues>, 'oracleConnectionStringType' | 'oracleTnsAlias' | 'oracleTnsDescriptor'>;

/**
 * What a TNS-mode Oracle connection records as its server when the (hidden, not applicable) Server field is empty: the
 * alias, or the descriptor's first HOST. The dial never reads it (`buildConnectString` uses the descriptor or alias,
 * `isLocalOracleConnection` classifies TNS as remote); the app shows `alias || server` as a connection's name in many
 * places, so an empty server would leave an unnamed connection nameless. The HOST is read by the descriptor PARSER
 * (first ADDRESS only): a regex scan picked a quoted `(HOST=…)` out of `(SECURITY=(MY_WALLET_PASSWORD="…"))` and stored
 * it as the server (security slice 2026-09-28). A descriptor it cannot read names nothing.
 */
function oracleTnsServer(target: TnsTarget): string {
  if (oracleConnectMode(target) === 'tns_alias') return target.oracleTnsAlias?.trim() ?? '';
  return oracleDescriptorAddress(target.oracleTnsDescriptor ?? '')?.host ?? '';
}

/**
 * The record a Save persists. A TNS-mode Oracle server is (re)synthesized from the current target when the Server is
 * empty, or when it is still the synthesized one: the draft's `oracleServerFromTnsTarget`, loaded with the record and
 * revoked by any user edit of Server (C0 reviews C0-02 and r2, C1 review C1-02). Provenance is never inferred from the
 * value: a typed server can equal the alias or the descriptor's host.
 */
export function buildConnection(values: ConnectionFormValues, id: string): Connection {
  if (values.engine === 'sqlite') {
    const filePath = values.server.trim();
    return {
      id,
      engine: 'sqlite',
      alias: values.alias,
      server: filePath,
      filePath,
      port: '0',
      authentication: 'No password',
      readOnly: values.readOnly ?? false,
      mcpPermissionLevel: values.mcpPermissionLevel || 'inherit',
      color: values.color,
    };
  }
  const server = values.server.trim();
  const tnsNamed = values.engine === 'oracle' && oracleConnectMode(values) !== 'ezconnect'
    && (!server || values.oracleServerFromTnsTarget === true);
  // A new target that names nothing (a descriptor without a HOST) keeps the old name rather than leave it nameless.
  const tnsServer = tnsNamed ? oracleTnsServer(values) || server : undefined;
  // S7-10: the File / Folder choice is form state, never saved; the provenance is recorded only while it holds.
  const { oracleServerFromTnsTarget: _provenance, ...record } = withoutTransientStorageFields(values);
  return {
    id,
    ...record,
    ...(tnsServer !== undefined ? { server: tnsServer, oracleServerFromTnsTarget: true } : {}),
    // C3-11: a Kusto server is the cluster address only — property text (an AppKey included) never reaches the record.
    // The form refuses such a Cluster URL first (`connectionFieldRules`); this keeps every other caller safe too.
    ...(values.engine === 'kusto' ? { server: withoutKustoProperties(server) } : {}),
    // C3.1: the origin describes an id; without one there is nothing to describe.
    ...(record.azureResourceId ? {} : { azureResourceIdOrigin: undefined }),
    readOnly: values.readOnly ?? false,
  };
}

/**
 * What this save says about itself. `passwordEdited` is the password field's TanStack `isDirty` (it cannot be inferred
 * from the value: the edit form loads the decrypted password, and main compares plaintext with ciphertext). The typed SSH
 * secrets ride beside the connection too (round 5, F5) — `stripTransientSshSecrets` takes them off the record.
 */
export function saveOptionsFor(values: ConnectionFormValues, passwordDirty: boolean): SaveConnectionOptions {
  const ssh = values.sshTunnel as { enabled?: boolean; password?: string; passphrase?: string } | undefined;
  const typed = ssh?.enabled ? ssh : undefined;
  return {
    passwordEdited: passwordDirty,
    ...(typed?.password ? { sshPassword: typed.password } : {}),
    ...(typed?.passphrase ? { sshPassphrase: typed.passphrase } : {}),
  };
}

/**
 * A throwaway PROBE connection for Test / Load. It never reuses the saved id (connecting and disconnecting the live id
 * through raw IPC would tear down the user's session, and the backend would reuse the live connection by id). The probe
 * id embeds the id this connection WILL be saved under, so what a probe earns (an Entra auth record) is filed where Save
 * and Connect look. The stored password is resolved at call time, so a probe fired before hydration still signs in.
 */
export async function buildProbeConnection(
  values: ConnectionFormValues,
  draftConnectionId: string,
  editConnection: Connection | undefined,
): Promise<{ connection: Connection } | { error: string }> {
  const probe: Connection = { ...buildConnection(normalizeValues(values), draftConnectionId), id: buildProbeConnectionId(draftConnectionId) };
  if (probe.engine === 'sqlite' || probe.password || !editConnection?.hasStoredPassword) return { connection: probe };
  let decrypted = '';
  try {
    decrypted = await getDecryptedPassword(editConnection.id);
  } catch {
    decrypted = '';
  }
  if (!decrypted) return { error: 'Could not retrieve the saved password. Re-enter it, then try again.' };
  return { connection: { ...probe, password: decrypted } };
}
